import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {TemplateFeature, BaseComponent} from 'compob2b-libs';
import {AbstractControl, FormArray, FormControl, FormGroup} from '@angular/forms';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {DamItemTemplateFeaturesService, TemplateFeatureValue} from '../../dam-item-template-features.service';

@Component({
  selector: 'app-tf-smartlist',
  templateUrl: './tf-smartlist.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfSmartlistComponent  extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;

  private smartlistValue: string = null;

  form = new FormGroup({
    items: new FormArray([])
  });

  constructor(protected readonly templateFeatureService: DamItemTemplateFeaturesService) {
    super();
  }

  ngOnInit(): void {
    this.setDefaultValue();

    this.form.get('items').valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        const filledData = data.filter(item => item.key && item.value);
        this.smartlistValue = filledData.length === 0 ? null : JSON.stringify(filledData);
        this.setFeature(this.smartlistValue, this.form.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue(false);
        this.cdr.detectChanges();
      });
  }

  private setDefaultValue(addItem: boolean = true): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel && featureModel[this.feature.id]) {
      const smartlist = JSON.parse(featureModel[this.feature.id].toString());
      if (smartlist) {
        this.items.clear({emitEvent: false});
        smartlist.forEach(item => this.items.push(this.newSmartlistItem(item.key, item.value)), {emitEvent: false});
        this.smartlistValue = smartlist.length === 0 ? null : JSON.stringify(smartlist);
      }

      this.setDefaultFeatureValue(JSON.stringify(this.items.value), this.form.valid, this.isShowEmptyWarning());
      return;
    }

    this.setDefaultFeatureValue(null, true, this.isRequired());
    if (addItem) {
      this.addSmartlistItem();
    }
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty,
      true
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  private newSmartlistItem(key: string, value: string): AbstractControl {
    return new FormGroup({
      key: new FormControl(key),
      value: new FormControl(value),
    });
  }

  addSmartlistItem(): void {
    this.items.push(this.newSmartlistItem(null, null));
  }

  deleteSmartlistItem(itemIndex: number): void {
    if (this.items.length === 1) {
      this.items.at(itemIndex).reset();
      return;
    }
    this.items.removeAt(itemIndex);
  }

  get smartlist(): any {
    return this.form.get('items');
  }

  isEmpty(): boolean {
    return this.smartlistValue === null;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }
}
